import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlay, faMusic, faPodcast, faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons';
import { GRADIENTS } from '../../config';
import type { YouTubeVideo } from '../../types';

interface VideoCardProps {
    video: YouTubeVideo;
    index?: number;
    type?: 'music' | 'podcast';
    onPlay?: (video: YouTubeVideo) => void;
    className?: string;
}

const VideoCard: React.FC<VideoCardProps> = ({
    video,
    index = 0,
    type = 'music',
    onPlay,
    className = ''
}) => {
    const [imageLoaded, setImageLoaded] = useState(false);
    const [imageError, setImageError] = useState(false);

    const handleClick = () => {
        if (onPlay) {
            onPlay(video);
        } else {
            window.open(`https://www.youtube.com/watch?v=${video.id}`, '_blank');
        }
    };

    const gradient = GRADIENTS[index % GRADIENTS.length];
    const icon = type === 'music' ? faMusic : faPodcast;

    return (
        <div
            onClick={handleClick}
            className={`
        group relative cursor-pointer
        animate-fade-in
        ${className}
      `}
            style={{ animationDelay: `${index * 75}ms` }}
        >
            {/* Card Container */}
            <div className="relative rounded-2xl overflow-hidden
        bg-gradient-to-br from-gray-800 to-gray-900
        shadow-lg shadow-black/30
        transform transition-all duration-500 ease-out
        group-hover:scale-[1.02] group-hover:shadow-2xl group-hover:shadow-amber-500/10
        ring-2 ring-transparent group-hover:ring-white/20
      ">
                {/* Thumbnail Container */}
                <div className="relative aspect-video overflow-hidden">
                    {/* Skeleton Loading */}
                    {!imageLoaded && !imageError && (
                        <div className="absolute inset-0 bg-gradient-to-br from-gray-700 to-gray-800 animate-pulse">
                            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent skeleton-shimmer" />
                        </div>
                    )}

                    {/* Thumbnail Image */}
                    <img
                        src={video.thumbnail}
                        alt={video.title}
                        className={`
              w-full h-full object-cover
              transition-all duration-700
              ${imageLoaded ? 'opacity-100 scale-100' : 'opacity-0 scale-110'}
              group-hover:scale-110
            `}
                        loading="lazy"
                        onLoad={() => setImageLoaded(true)}
                        onError={(e) => {
                            setImageError(true);
                            if (video.thumbnailFallback) {
                                (e.target as HTMLImageElement).src = video.thumbnailFallback;
                            }
                        }}
                    />

                    {/* Gradient Overlay */}
                    <div className={`absolute inset-0 bg-gradient-to-br ${gradient} 
            opacity-0 group-hover:opacity-30 transition-opacity duration-500`} />

                    {/* Dark overlay for better contrast */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />

                    {/* Play Button */}
                    <div className="absolute inset-0 flex items-center justify-center
            opacity-0 group-hover:opacity-100 transition-all duration-300">
                        <div className={`
              p-4 sm:p-5 lg:p-6 rounded-full
              bg-gradient-to-br ${gradient}
              shadow-2xl
              transform scale-75 group-hover:scale-100
              transition-all duration-300
            `}>
                            <FontAwesomeIcon icon={faPlay} className="text-white text-xl sm:text-2xl lg:text-3xl ml-1" />
                        </div>
                    </div>

                    {/* Type Badge */}
                    <div className="absolute top-3 left-3 sm:top-4 sm:left-4
            flex items-center gap-2 px-3 py-1.5 rounded-full
            bg-black/60 backdrop-blur-sm">
                        <FontAwesomeIcon icon={icon} className="text-amber-400 text-sm" />
                        <span className="text-white text-xs font-medium capitalize">{type}</span>
                    </div>

                    {/* External Link Indicator */}
                    <div className="absolute top-3 right-3 sm:top-4 sm:right-4
            p-2 rounded-full bg-black/60 backdrop-blur-sm
            opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <FontAwesomeIcon icon={faExternalLinkAlt} className="text-white text-xs" />
                    </div>
                </div>

                {/* Content */}
                <div className="p-4 sm:p-5">
                    <h3 className="font-bold text-white text-sm sm:text-base lg:text-lg 
            line-clamp-2 group-hover:text-amber-400 transition-colors duration-300">
                        {video.title}
                    </h3>

                    {video.description && (
                        <p className="mt-2 text-gray-400 text-xs sm:text-sm line-clamp-2">
                            {video.description}
                        </p>
                    )}

                    {/* Duration / Views placeholder */}
                    <div className="mt-3 flex items-center gap-3 text-gray-500 text-xs">
                        <span className="flex items-center gap-1">
                            <FontAwesomeIcon icon={icon} className="text-gray-600" />
                            <span>YouTube</span>
                        </span>
                    </div>
                </div>

                {/* Animated border on hover */}
                <div className={`absolute inset-0 rounded-2xl 
          bg-gradient-to-r ${gradient} opacity-0 group-hover:opacity-100
          transition-opacity duration-500 -z-10 blur-xl`} />
            </div>
        </div>
    );
};

export default VideoCard;
